/**
 * OmniFlow Density System
 *
 * Controls UI density via CSS custom properties on a context wrapper.
 * Three modes:
 *   - compact    → tighter spacing, smaller text (power-user / small screens)
 *   - comfortable → default (most users)
 *   - spacious   → more breathing room (presentation / accessibility)
 *
 * Usage:
 *   // Wrap the app (or a section):
 *   <DensityProvider density="compact">…</DensityProvider>
 *
 *   // Read in any child component:
 *   const density = useDensity(); // 'compact' | 'comfortable' | 'spacious'
 *
 *   // Or use the CSS vars directly in Tailwind:
 *   style={{ padding: 'var(--density-padding)' }}
 */
import { type ReactNode, createContext, useContext, useEffect, useRef } from 'react';

export type Density = 'compact' | 'comfortable' | 'spacious';

interface DensityContextValue {
  density: Density;
}

const DensityContext = createContext<DensityContextValue>({ density: 'comfortable' });

/**
 * CSS variable values per density tier.
 * These are applied to the wrapper element so they cascade to all children.
 */
const DENSITY_VARS: Record<Density, Record<string, string>> = {
  compact: {
    '--density-padding': '6px',
    '--density-padding-sm': '4px',
    '--density-padding-lg': '10px',
    '--density-gap': '6px',
    '--density-gap-sm': '4px',
    '--density-gap-lg': '10px',
    '--density-row-height': '32px',
    '--density-font-size-sm': '11px',
    '--density-font-size': '12px',
    '--density-font-size-lg': '13px',
    '--density-radius': '4px',
  },
  comfortable: {
    '--density-padding': '10px',
    '--density-padding-sm': '6px',
    '--density-padding-lg': '16px',
    '--density-gap': '10px',
    '--density-gap-sm': '6px',
    '--density-gap-lg': '16px',
    '--density-row-height': '40px',
    '--density-font-size-sm': '11.5px',
    '--density-font-size': '13px',
    '--density-font-size-lg': '14px',
    '--density-radius': '6px',
  },
  spacious: {
    '--density-padding': '16px',
    '--density-padding-sm': '10px',
    '--density-padding-lg': '24px',
    '--density-gap': '16px',
    '--density-gap-sm': '10px',
    '--density-gap-lg': '24px',
    '--density-row-height': '48px',
    '--density-font-size-sm': '12px',
    '--density-font-size': '14px',
    '--density-font-size-lg': '15px',
    '--density-radius': '8px',
  },
};

interface DensityProviderProps {
  density: Density;
  children: ReactNode;
  /** Optionally pass a className to the wrapper div */
  className?: string;
}

export function DensityProvider({ density, children, className }: DensityProviderProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const vars = DENSITY_VARS[density];
    for (const [k, v] of Object.entries(vars)) {
      el.style.setProperty(k, v);
    }
    // Clean up: remove vars when density changes before re-applying
    return () => {
      const allKeys = Object.keys(DENSITY_VARS.comfortable);
      for (const k of allKeys) {
        el.style.removeProperty(k);
      }
    };
  }, [density]);

  return (
    <DensityContext.Provider value={{ density }}>
      <div ref={ref} className={className} data-density={density}>
        {children}
      </div>
    </DensityContext.Provider>
  );
}

/** Read the current density in any child component */
export function useDensity(): Density {
  return useContext(DensityContext).density;
}
